import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import '../css/toast.css';

const ToastContext = createContext(null);

const asDialog = (options, defaults) => {
  if (typeof options === 'string') {
    return { ...defaults, title: options };
  }
  return { ...defaults, ...(options || {}) };
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const resolverRef = useRef(null);
  const dialogModeRef = useRef(null);

  const addToast = useCallback((message, type = 'success', duration = 2500) => {
    const text = typeof message === 'string' ? message.trim() : '';
    if (!text) return;
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message: text, type: type || 'success' }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, duration);
  }, []);

  const settlePending = useCallback(() => {
    const resolve = resolverRef.current;
    if (!resolve) return;
    resolverRef.current = null;
    resolve(dialogModeRef.current === 'prompt' ? null : false);
  }, []);

  const closeDialog = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    dialogModeRef.current = null;
    setDialog(null);
    setInputValue('');
    if (resolve) resolve(result);
  }, []);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      settlePending();
      dialogModeRef.current = 'confirm';
      resolverRef.current = resolve;
      setDialog(asDialog(options, {
        mode: 'confirm',
        title: 'Please confirm',
        message: '',
        confirmLabel: 'Yes',
        cancelLabel: 'Cancel',
        danger: true
      }));
    });
  }, [settlePending]);

  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      const next = asDialog(options, {
        mode: 'prompt',
        title: 'Enter a value',
        message: '',
        placeholder: '',
        confirmLabel: 'Save',
        cancelLabel: 'Cancel',
        danger: false,
        defaultValue: ''
      });
      settlePending();
      dialogModeRef.current = 'prompt';
      resolverRef.current = resolve;
      setInputValue(next.defaultValue || '');
      setDialog(next);
    });
  }, [settlePending]);

  useEffect(() => {
    if (!dialog) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDialog(dialog.mode === 'prompt' ? null : false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [dialog, closeDialog]);

  const cancelDialog = () => closeDialog(dialog?.mode === 'prompt' ? null : false);

  return (
    <ToastContext.Provider value={{ addToast, confirm, prompt }}>
      {children}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            {t.message}
          </div>
        ))}
      </div>
      {dialog && (
        <div className="toast-dialog-backdrop" onClick={cancelDialog}>
          <div
            className={`toast-dialog ${dialog.danger ? 'toast-dialog-danger' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="toast-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="toast-dialog-title">{dialog.title}</h3>
            {dialog.message ? <p>{dialog.message}</p> : null}
            {dialog.mode === 'prompt' && (
              <textarea
                className="toast-dialog-input"
                autoFocus
                rows={3}
                placeholder={dialog.placeholder}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            )}
            <div className="toast-dialog-actions">
              <button type="button" className="toast-dialog-cancel" onClick={cancelDialog}>
                {dialog.cancelLabel}
              </button>
              <button
                type="button"
                className={`toast-dialog-confirm ${dialog.danger ? 'is-danger' : ''}`}
                onClick={() => closeDialog(dialog.mode === 'prompt' ? inputValue : true)}
              >
                {dialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      addToast: () => {},
      confirm: async () => false,
      prompt: async () => null
    };
  }
  return ctx;
};
